import { useSuspenseQuery } from "@tanstack/react-query";

import * as types from "types";
import useGetAccessToken from "components/Login/useGetAccessToken";
import { getThreadMessages } from "../methods/getThreadMessages";
import { MESSAGES_READ_OPTIONS } from "./messagesReadOptions";

interface UseMessagesSuspenseQueryOptions {
  threadId: types.ChatThread["id"];
}

/**
 * Suspense-flavored variant of useMessagesQuery. Throws a promise while the
 * messages list is loading and an error if the request fails. Callers
 * receive a non-nullable `data`. Shares the same query key as
 * useMessagesQuery, so mutations and the non-Suspense hook see one cache.
 */
export default function useMessagesSuspenseQuery({
  threadId,
}: UseMessagesSuspenseQueryOptions) {
  const getAccessToken = useGetAccessToken();

  return useSuspenseQuery({
    ...MESSAGES_READ_OPTIONS,
    queryKey: ["cc-db-api", "threads", threadId, "messages"],
    queryFn: async () => {
      // Defer to a microtask so MSAL's setState (triggered by getAccessToken
      // inside the queryFn) doesn't fire synchronously during the render that
      // suspends.
      // https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/5796#issuecomment-1763461620
      await Promise.resolve();
      return getThreadMessages(threadId as string, getAccessToken);
    },
    refetchOnWindowFocus: false,
  });
}
